import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { toast } from 'sonner';
import { MoreHorizontal, CheckCircle2, XCircle, Loader2, Trash2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { REQUEST_TYPES } from '@/lib/site';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';

const STATUS_STYLES = {
    nuovo: 'bg-atelier-clay/40 text-foreground border-atelier-clay/50',
    confermato: 'bg-atelier-sage/25 text-atelier-sage-ink border-atelier-sage/40',
    annullato: 'bg-muted text-muted-foreground border-border',
};

function typeLabel(v) {
    return REQUEST_TYPES.find((t) => t.value === v)?.label || v;
}

function safeDate(iso, fmt = 'd MMM yyyy') {
    try { return format(new Date(iso), fmt, { locale: itLocale }); } catch { return iso || '-'; }
}

export default function AppointmentsPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/appointments');
            setItems(res.data || []);
        } catch (e) {
            toast.error('Impossibile caricare gli appuntamenti');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.put(`/admin/appointments/${id}`, { status });
            toast.success('Stato aggiornato');
            load();
        } catch (e) {
            toast.error('Errore aggiornamento stato');
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Eliminare definitivamente questo appuntamento?')) return;
        try {
            await api.delete(`/admin/appointments/${id}`);
            toast.success('Eliminato');
            load();
        } catch (e) {
            toast.error('Errore eliminazione');
        }
    };

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                <h2 className="font-serif text-xl">Richieste appuntamento</h2>
                <span className="text-xs text-muted-foreground">{items.length} totali</span>
            </div>
            <div className="overflow-x-auto">
                <Table data-testid={TEST_IDS.adminAppointmentsTable}>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Data consulenza</TableHead>
                            <TableHead>Cliente</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Contatti</TableHead>
                            <TableHead>Stato</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento…</TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Nessuna richiesta al momento.</TableCell></TableRow>
                        ) : items.map((a) => (
                            <TableRow key={a.id} className="hover:bg-secondary/40 cursor-pointer" onClick={() => setSelected(a)}>
                                <TableCell>
                                    <div className="text-sm">{safeDate(a.slot_date + 'T00:00:00')}</div>
                                    <div className="text-xs text-muted-foreground">{a.slot_time}</div>
                                </TableCell>
                                <TableCell><div className="font-medium">{a.full_name}</div></TableCell>
                                <TableCell><span className="text-sm">{typeLabel(a.request_type)}</span></TableCell>
                                <TableCell>
                                    <div className="text-xs text-muted-foreground">{a.email}</div>
                                    <div className="text-xs text-muted-foreground">{a.phone}</div>
                                </TableCell>
                                <TableCell><Badge variant="outline" className={cn('text-xs', STATUS_STYLES[a.status])}>{a.status}</Badge></TableCell>
                                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => setStatus(a.id, 'confermato')}><CheckCircle2 className="h-4 w-4 mr-2 text-atelier-sage-ink" />Conferma</DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => setStatus(a.id, 'nuovo')}>Rimetti come nuovo</DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => setStatus(a.id, 'annullato')}><XCircle className="h-4 w-4 mr-2 text-destructive" />Annulla</DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => remove(a.id)}><Trash2 className="h-4 w-4 mr-2" />Elimina</DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-2xl">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl">{selected.full_name}</DialogTitle>
                                <DialogDescription>
                                    {typeLabel(selected.request_type)} · Consulenza il {safeDate(selected.slot_date + 'T00:00:00')} alle {selected.slot_time}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 text-sm">
                                <div><p className="eyebrow mb-1">Email</p><p>{selected.email}</p></div>
                                <div><p className="eyebrow mb-1">Telefono</p><p>{selected.phone}</p></div>
                                {selected.event_date && <div><p className="eyebrow mb-1">Data evento</p><p>{safeDate(selected.event_date + 'T00:00:00')}</p></div>}
                                {selected.location && <div><p className="eyebrow mb-1">Location</p><p>{selected.location}</p></div>}
                                {selected.num_guests != null && <div><p className="eyebrow mb-1">Invitati</p><p>{selected.num_guests}</p></div>}
                                {selected.budget && <div><p className="eyebrow mb-1">Budget</p><p>{selected.budget}</p></div>}
                                {selected.style && <div className="sm:col-span-2"><p className="eyebrow mb-1">Stile</p><p>{selected.style}</p></div>}
                                {selected.notes && <div className="sm:col-span-2"><p className="eyebrow mb-1">Note</p><p className="whitespace-pre-line">{selected.notes}</p></div>}
                                {selected.inspiration_image_ids?.length > 0 && (
                                    <div className="sm:col-span-2"><p className="eyebrow mb-1">Ispirazioni salvate</p><p>{selected.inspiration_image_ids.length} immagini selezionate dalla galleria</p></div>
                                )}
                                <div className="sm:col-span-2"><p className="eyebrow mb-1">Stato</p><Badge variant="outline" className={cn('text-xs', STATUS_STYLES[selected.status])}>{selected.status}</Badge></div>
                            </div>
                            <DialogFooter className="mt-4 gap-2">
                                <Button variant="outline" onClick={() => setStatus(selected.id, 'annullato').then(() => setSelected(null))}>Annulla appuntamento</Button>
                                <Button onClick={() => setStatus(selected.id, 'confermato').then(() => setSelected(null))}>Conferma</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
